// NotificationBell.js — In-app notification bell for AppBar
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  List,
  ListItemButton,
  Tabs,
  Tab,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Circle as CircleIcon,
  Close as CloseIcon,
  BeachAccess as LeaveIcon,
  Payment as PayrollIcon,
  Warning as AlertIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  AccessTime as AccessTimeIcon,
  Savings as SavingsIcon,
  AccountBalanceWallet as WalletIcon,
  Stars as StarsIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { getUnreadCount, getNotifications, markAsRead, markAllAsRead, markNotificationsSeen, subscribeToNotifications } from '../services/notificationService';
import { formatRelativeDateTime } from '../utils/formatters';
import { tokens } from '../theme/tokens';

// Tab filters on the flat (chronological) notification list
const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'approvals', label: 'Approvals' },
  { value: 'system', label: 'System' },
];

const getGroupKey = (type) => {
  if (type === 'expiry_alert') return 'expiry_alert';
  if (type === 'system') return 'system';
  if (['approval_needed', 'approved', 'rejected', 'payroll_released'].includes(type)) return 'approvals';
  return 'other';
};

// approval_needed spans leave, OT, advances, receipts and appraisals - pick
// the icon from related_table so e.g. a submitted receipt never wears the
// leave umbrella (BeachAccess). Each glyph is visually distinct.
const APPROVAL_TYPE_ICONS = {
  leave_applications: <LeaveIcon fontSize="small" />, // umbrella = leave
  overtime_requests: <AccessTimeIcon fontSize="small" />, // clock = OT
  advances: <SavingsIcon fontSize="small" />, // piggy bank = advance
  petty_cash_advances: <WalletIcon fontSize="small" />, // wallet = staff float
  expense_claims: <ReceiptIcon fontSize="small" />, // receipt = claim
  petty_cash: <ReceiptIcon fontSize="small" />, // receipt = receipt
  company_expenses: <ReceiptIcon fontSize="small" />, // receipt = company expense
  appraisals: <StarsIcon fontSize="small" />, // stars = appraisal
};

const getTypeIcon = (type, relatedTable) => {
  if (type === 'approval_needed') {
    return APPROVAL_TYPE_ICONS[relatedTable] || <AssignmentIcon fontSize="small" />;
  }
  switch (type) {
    case 'approved': return <ApprovedIcon fontSize="small" />;
    case 'rejected': return <RejectedIcon fontSize="small" />;
    case 'payroll_released': return <PayrollIcon fontSize="small" />;
    case 'expiry_alert': return <AlertIcon fontSize="small" />;
    case 'system': return <InfoIcon fontSize="small" />;
    default: return <CircleIcon fontSize="small" />;
  }
};

// Tonal icon box colors per category (bg = semantic lighter surface)
const getTypeStyle = (type) => {
  switch (type) {
    case 'approval_needed': return { bg: 'warning.lighter', fg: 'warning.main' };
    case 'approved': return { bg: 'success.lighter', fg: 'success.main' };
    case 'rejected': return { bg: 'error.lighter', fg: 'error.main' };
    case 'payroll_released': return { bg: 'primary.lighter', fg: 'primary.main' };
    case 'expiry_alert':
    case 'document_expired': return { bg: 'warning.lighter', fg: 'warning.main' };
    case 'system': return { bg: 'info.lighter', fg: 'info.main' };
    default: return { bg: 'grey.100', fg: 'grey.600' };
  }
};

export default function NotificationBell() {
  const { user } = useAuth();
  const { selectedCompanyId } = useCompany();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [listFilter, setListFilter] = useState('all');
  // Newest-notification toast shown in-tab (live, no reload needed)
  const [toastNotification, setToastNotification] = useState(null);
  // Mirrors anchorEl for the subscription callback (created once) so the
  // toast is suppressed while the user is already looking at the popover.
  const anchorElRef = useRef(null);
  anchorElRef.current = anchorEl;
  // Last time the popover was opened ("seen"). The badge counts notifications
  // created AFTER this timestamp; persisted so a refresh keeps the badge clear.
  const seenAtRef = useRef((() => {
    try { return localStorage.getItem('notif_last_seen_at'); } catch { return null; }
  })());

  const fetchUnread = useCallback(async () => {
    if (!user?.id) return;
    const count = await getUnreadCount(user.id, selectedCompanyId, seenAtRef.current);
    setUnreadCount(count);
  }, [user?.id, selectedCompanyId]);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    const result = await getNotifications(user.id, { limit: 50, companyId: selectedCompanyId });
    if (result.success) {
      setNotifications(result.data);
    } else {
      console.error('[NotificationBell] Failed to fetch notifications:', result.error);
    }
  }, [user?.id, selectedCompanyId]);

  useEffect(() => {
    if (!user?.id) return;

    fetchUnread();
    fetchNotifications();

    const subscription = subscribeToNotifications(user.id, (newNotification) => {
      setUnreadCount((prev) => prev + 1);
      setNotifications((prev) => [newNotification, ...prev]);

      // In-tab auto-show: pop the newest notification as a toast while the
      // user is actively in the app (no page reload needed). Backgrounded
      // tabs keep using the browser Notification below instead.
      if (!document.hidden && !anchorElRef.current) {
        setToastNotification(newNotification);
      }

      // Browser push when the tab is hidden/minimized
      if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
        try {
          const notif = new Notification(newNotification.title || 'Core System', {
            body: newNotification.message || '',
            icon: '/logo192.png',
            badge: '/favicon.ico',
            tag: newNotification.id || `notif-${Date.now()}`,
            requireInteraction: false,
          });
          notif.onclick = () => {
            window.focus();
            if (newNotification.link) {
              window.location.hash = '';
              window.location.pathname = newNotification.link;
            }
          };
        } catch (e) {
          console.warn('[NotificationBell] Browser notification failed:', e);
        }
      }
    });

    const intervalId = setInterval(() => {
      fetchUnread();
      fetchNotifications();
    }, 30000);

    return () => {
      if (subscription) subscription.unsubscribe();
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, selectedCompanyId]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    // Mark as seen: the badge clears IMMEDIATELY (items keep their unread dot
    // until clicked or "Mark all read"), and the server is told in the
    // background so other devices agree on the badge count.
    const now = new Date().toISOString();
    seenAtRef.current = now;
    try { localStorage.setItem('notif_last_seen_at', now); } catch { /* ignore */ }
    setUnreadCount(0);
    fetchNotifications();
    markNotificationsSeen();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllRead = async () => {
    if (!user?.id) return;
    await markAllAsRead(user.id);
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const handleClickNotification = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    handleClose();
    if (notification.link) {
      let url = notification.link;
      if (notification.type === 'approval_needed' && !url.includes('tab=')) {
        const tabSep = url.includes('?') ? '&' : '?';
        url = `${url}${tabSep}tab=need_approval`;
      }
      if (notification.related_record_id) {
        sessionStorage.setItem('__flash_target__', notification.related_record_id);
      }
      navigate(url);
    }
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  // Flat chronological list (most recent first) with tab filters. The
  // "system" tab shows everything that isn't an approval so no item becomes
  // unreachable.
  const filteredNotifications = useMemo(() => {
    const sorted = [...notifications].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    if (listFilter === 'all') return sorted;
    return sorted.filter((n) => {
      const key = getGroupKey(n.type);
      return listFilter === 'approvals' ? key === 'approvals' : key !== 'approvals';
    });
  }, [notifications, listFilter]);

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label="Notifications">
        <Badge
          badgeContent={
            unreadCount > 0 ? (
              <>
                {/* Ping animation while there are unseen notifications */}
                <Box
                  component="span"
                  className="notification-badge-ping"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    borderRadius: '999px',
                    bgcolor: 'error.main',
                    opacity: 0.75,
                  }}
                />
                <Box component="span" sx={{ position: 'relative' }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Box>
              </>
            ) : (
              unreadCount || 0
            )
          }
          max={9}
          sx={{
            '& .MuiBadge-badge': {
              color: 'white',
              bgcolor: 'error.main',
              fontSize: '10px',
              fontWeight: 700,
              minWidth: 16,
              height: 16,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '999px',
              // ring-2 ring-blue-600 + shadow-sm
              boxShadow: `0 0 0 2px ${tokens.color.primary}, 0 1px 2px rgba(0, 0, 0, 0.15)`,
            },
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 384 },
            maxHeight: 480,
            borderRadius: '10px',
            border: '1px solid',
            borderColor: 'grey.200',
            overflow: 'hidden',
            boxShadow: 6,
            display: 'flex',
            flexDirection: 'column',
            // Menu wraps its children in a <ul>; turn it into a pinned-column
            // so header/tabs/footer stay fixed while the list scrolls.
            '& > ul': {
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header: title + Mark all read + close */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={handleMarkAllRead}
                sx={{ textTransform: 'none', color: 'primary.main', fontSize: '12px', minWidth: 0 }}
              >
                Mark all read
              </Button>
            )}
            <IconButton size="small" onClick={handleClose} aria-label="Close notifications">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Tab filters (replaces category accordions) */}
        <Tabs
          value={listFilter}
          onChange={(_, v) => setListFilter(v)}
          variant="fullWidth"
          sx={{
            minHeight: 36,
            flexShrink: 0,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 36,
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
            },
          }}
        >
          {FILTERS.map((f) => (
            <Tab key={f.value} value={f.value} label={f.label} />
          ))}
        </Tabs>

        {notifications.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ py: 0, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {filteredNotifications.map((notification) => {
              const unread = !notification.is_read;
              const iconStyle = getTypeStyle(notification.type);
              return (
                <ListItemButton
                  key={notification.id}
                  onClick={() => handleClickNotification(notification)}
                  sx={{
                    alignItems: 'flex-start',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'grey.100',
                    bgcolor: unread ? 'primary.lighter' : 'transparent',
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: unread ? 'primary.lighter' : 'grey.50' },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      bgcolor: iconStyle.bg,
                      color: iconStyle.fg,
                    }}
                  >
                    {getTypeIcon(notification.type, notification.related_table)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={unread ? 600 : 400} noWrap sx={{ flex: 1 }}>
                        {notification.title}
                      </Typography>
                      {unread && (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            flexShrink: 0,
                            borderRadius: '999px',
                            bgcolor: 'primary.main',
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '10px' }}>
                      {formatRelativeDateTime(notification.created_at)}
                    </Typography>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}

        <Divider sx={{ flexShrink: 0 }} />
        <Box sx={{ p: 1, flexShrink: 0 }}>
          <Button
            size="small"
            onClick={handleViewAll}
            fullWidth
            sx={{ textTransform: 'none', fontSize: '12px' }}
          >
            View All Notifications
          </Button>
        </Box>
      </Menu>

      {/* Live "new notification" toast - auto-shows the newest entry in-tab */}
      <Snackbar
        open={Boolean(toastNotification)}
        autoHideDuration={6000}
        onClose={() => setToastNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={(e) => {
            e?.stopPropagation?.();
            setToastNotification(null);
          }}
          onClick={() => {
            const n = toastNotification;
            if (n) {
              setToastNotification(null);
              handleClickNotification(n);
            }
          }}
          sx={{ cursor: 'pointer', alignItems: 'flex-start', width: { xs: 'calc(100vw - 32px)', sm: 380 } }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {toastNotification?.title}
          </Typography>
          <Typography variant="caption" display="block" sx={{ opacity: 0.9, mt: 0.5 }}>
            {toastNotification?.message}
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
}
